import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Phase E: Manager evening return-stock entry. Shows today's records, lets manager
// type returned-qty per product. Auto-warns on Loaded - Sold ≠ Returned.
export default function StockReturn() {
  const nav = useNavigate();
  const { t, lang, displayName } = useLang();
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [returns, setReturns] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get("/stock/today"),
      api.get("/products"),
    ]);
    const list = Array.isArray(r.data) ? r.data : (r.data ? [r.data] : []);
    setRecords(list);
    setProducts(p.data || []);
  };
  useEffect(() => { load(); }, []);

  const openRecord = (rec) => {
    setActive(rec);
    const r = {};
    rec.items.forEach(it => {
      // pre-fill with expected_remaining if not yet entered
      r[it.product_id] = it.returned_qty != null ? String(it.returned_qty) : String(it.expected_remaining ?? 0);
    });
    setReturns(r);
  };

  const setReturnQty = (id, raw) => {
    const cleaned = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    setReturns(prev => ({ ...prev, [id]: cleaned }));
  };

  const submit = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const items = Object.entries(returns).map(([pid, q]) => ({
        product_id: pid,
        returned_qty: parseInt(q || "0", 10) || 0,
      }));
      await api.put(`/manager/stock/return/${active.id}`, { items });
      toast.success(lang === "ta" ? "திரும்ப ஸ்டாக் சேமிக்கப்பட்டது" : "Return saved");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  // Detail view
  if (active) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pb-32 page-slide-in">
        <TopBar title={active.salesman_name} onBack={() => setActive(null)} />
        <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
          <div className="bg-white rounded-3xl p-5 soft-shadow">
            <div className="text-sm text-[#86868B] mb-1">{active.depot_name || "—"} • {active.date}</div>
            <div className="text-xs text-[#86868B] uppercase tracking-wider">{lang === "ta" ? "மொத்தம்" : "Totals"}</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="bg-blue-50 rounded-xl p-2 text-center">
                <div className="text-xs text-[#86868B]">{lang === "ta" ? "ஏற்றியது" : "Loaded"}</div>
                <div className="heading-font text-xl font-bold text-[#0055FF]">{active.totals?.loaded || 0}</div>
              </div>
              <div className="bg-green-50 rounded-xl p-2 text-center">
                <div className="text-xs text-[#86868B]">{lang === "ta" ? "விற்றது" : "Sold"}</div>
                <div className="heading-font text-xl font-bold text-[#34C759]">{active.totals?.sold || 0}</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-2 text-center">
                <div className="text-xs text-[#86868B]">{lang === "ta" ? "மீதம்" : "Remaining"}</div>
                <div className="heading-font text-xl font-bold text-[#FF9500]">{active.totals?.remaining || 0}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="stockreturn-products">
            <div className="heading-font font-bold text-base text-[#1D1D1F] mb-2">
              {lang === "ta" ? "திரும்ப கொடுத்த எண்ணிக்கை" : "Returned Boxes"}
            </div>
            {active.items.map(it => {
              const ret = parseInt(returns[it.product_id] || "0", 10) || 0;
              const mismatch = ret - (it.expected_remaining || 0);
              return (
                <div key={it.product_id} className="space-y-1.5" data-testid={`stockreturn-row-${it.product_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold heading-font text-[#1D1D1F] truncate">
                        {lang === "ta" ? (it.product_name_ta || it.product_name) : it.product_name}
                      </div>
                      <div className="text-[11px] text-[#86868B]">
                        {lang === "ta"
                          ? `ஏற்றம் ${it.loaded_qty} • விற்றது ${it.sold_qty} • எதிர்பார்த்தது ${it.expected_remaining}`
                          : `Loaded ${it.loaded_qty} • Sold ${it.sold_qty} • Expected ${it.expected_remaining}`}
                      </div>
                    </div>
                    <input
                      data-testid={`stockreturn-qty-${it.product_id}`}
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={returns[it.product_id] || ""}
                      onChange={(e) => setReturnQty(it.product_id, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
                      className="w-24 h-12 text-center text-xl font-bold rounded-2xl bg-[#F5F5F7] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none transition"
                    />
                  </div>
                  {mismatch !== 0 && (
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${mismatch < 0 ? "text-red-600" : "text-yellow-700"}`} data-testid={`stockreturn-mismatch-${it.product_id}`}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {lang === "ta"
                        ? `பொருந்தாது: ${mismatch > 0 ? "+" : ""}${mismatch}`
                        : `Mismatch: ${mismatch > 0 ? "+" : ""}${mismatch}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 glass border-t border-white/40 p-4">
          <button
            data-testid="stockreturn-submit"
            disabled={busy}
            onClick={submit}
            className="max-w-2xl mx-auto block w-full bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl px-6 py-3 font-bold shadow-md"
          >
            {t("save")}
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar
        title={lang === "ta" ? "மாலை ஸ்டாக் திரும்பம்" : "PM Stock Return"}
        onBack={() => nav("/manager")}
      />
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-3 fade-up">
        {records.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center soft-shadow text-[#86868B]" data-testid="stockreturn-empty">
            {lang === "ta" ? "இன்று ஸ்டாக் ஏற்றம் இல்ல" : "No stock loaded today"}
          </div>
        ) : (
          records.map(r => {
            const done = r.items.every(it => it.returned_qty != null);
            const totalMismatch = (r.items || []).reduce((s, it) => s + (Math.abs(it.mismatch || 0)), 0);
            return (
              <button
                key={r.id}
                onClick={() => openRecord(r)}
                data-testid={`stockreturn-card-${r.id}`}
                className="w-full bg-white rounded-2xl p-4 soft-shadow text-left hover:shadow-md transition flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-[#0055FF]/10 grid place-items-center">
                  <Boxes className="w-5 h-5 text-[#0055FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="heading-font font-bold text-[#1D1D1F]">{r.salesman_name}</div>
                  <div className="text-xs text-[#86868B]">
                    {lang === "ta" ? "ஏற்றம்" : "Loaded"} {r.totals?.loaded || 0} • {lang === "ta" ? "விற்றது" : "Sold"} {r.totals?.sold || 0}
                  </div>
                </div>
                {done ? (
                  totalMismatch === 0 ? (
                    <span className="pill bg-green-50 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> {lang === "ta" ? "சரி" : "OK"}</span>
                  ) : (
                    <span className="pill bg-orange-50 text-orange-700"><AlertTriangle className="w-3.5 h-3.5" /> ±{totalMismatch}</span>
                  )
                ) : (
                  <span className="pill bg-blue-50 text-blue-700">{lang === "ta" ? "திற" : "Open"}</span>
                )}
                <ChevronRight className="w-4 h-4 text-[#86868B]" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
